import { useMemo } from 'react'
import { useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import {
  selectAgentConversationFormCaselawSearchFilter,
  selectAgentConversationFormIncludeUnpublishedCases,
  selectAgentConversationFormLRRSearchFilter,
  selectAgentV2NextTurnFileIds,
  selectAgentV2NextTurnWritingSampleFileId,
  selectAgentConversationFormWebSearchEnabled,
} from '@/agent-v2/chat-form/store/selectors'
import { buildAgentV2SearchDefaultsFromAgentFilters } from '@/agent-v2/utils/agent-v2-search-defaults'
import type { AgentV2SearchDefaults } from '@/agent-v2/store/schemas'

/** The research-filter and file-selection state a turn submits alongside its message, live-derived for `conversationId`. */
export function useAgentSearchDefaults(conversationId: string): AgentV2SearchDefaults {
  const caselawSearchFilter = useAppSelector((state: RootState) => selectAgentConversationFormCaselawSearchFilter(state, { conversationId }))
  const lrrSearchFilter = useAppSelector((state: RootState) => selectAgentConversationFormLRRSearchFilter(state, { conversationId }))
  const includeUnpublishedCases = useAppSelector((state: RootState) => selectAgentConversationFormIncludeUnpublishedCases(state, { conversationId }))
  const fileIds = useAppSelector((state: RootState) => selectAgentV2NextTurnFileIds(state, { conversationId }))
  const writingSampleFileId = useAppSelector((state: RootState) => selectAgentV2NextTurnWritingSampleFileId(state, { conversationId }))
  const webSearchEnabled = useAppSelector((state: RootState) => selectAgentConversationFormWebSearchEnabled(state, { conversationId }))

  return useMemo(
    () =>
      buildAgentV2SearchDefaultsFromAgentFilters({
        caselawSearchFilter,
        includeUnpublishedCases,
        lrrSearchFilter,
        fileIds,
        writingSampleFileId,
        webSearchEnabled,
      }),
    [caselawSearchFilter, includeUnpublishedCases, lrrSearchFilter, fileIds, writingSampleFileId, webSearchEnabled]
  )
}

/**
 * The same values read straight from the store, for code that continues after an await and can no
 * longer rely on what its render saw (a host's `prepareSubmission` may attach files meanwhile).
 */
export function getAgentSearchDefaults(state: RootState, conversationId: string): AgentV2SearchDefaults {
  return buildAgentV2SearchDefaultsFromAgentFilters({
    caselawSearchFilter: selectAgentConversationFormCaselawSearchFilter(state, { conversationId }),
    includeUnpublishedCases: selectAgentConversationFormIncludeUnpublishedCases(state, { conversationId }),
    lrrSearchFilter: selectAgentConversationFormLRRSearchFilter(state, { conversationId }),
    fileIds: selectAgentV2NextTurnFileIds(state, { conversationId }),
    writingSampleFileId: selectAgentV2NextTurnWritingSampleFileId(state, { conversationId }),
    webSearchEnabled: selectAgentConversationFormWebSearchEnabled(state, { conversationId }),
  })
}
